// Libraries
import React, { MouseEvent, useState, useEffect } from 'react'

// Images
import downArrowWhite from '../../../../static/steam-img/arrows/down_arrow_white.svg'
import upArrowWhite from '../../../../static/steam-img/arrows/up_arrow_white.svg'
import logoWhite from '../../../../static/steam-img/logo_uipath-white.svg'
import closeIconBlue from '../../../../static/steam-img/close_icon_blue.svg'

// Constants
import { ModuleNames } from '../../../constants/componentNames.constants'
import {
  LocaleCodes,
  LanguageSelect,
} from '../../../constants/localization.constants'

// Helpers
import {
  createLinkBasedOnType,
  isExternalURL,
  internalLinkHandler,
} from '../../../utils/link.utils'
import { mapLocale } from '../../../utils/pageCreation.utils'
import footerStaticQuery from './footerStaticQuery'
import { returnMatchingLocaleInfo } from '../../../utils/functions.utils'

// Styles
import { FooterWrapper } from './style'
import WrapperImage from '../wrapperImage'

export interface NavigationResult {
  entryLocale: any
  location: any
  menuGroups: any
  name: any
  node_locale: any
  legalFooterOnly: any
  socialMediaFooterOnly: any
}

const Footer = ({ minimalFooter, locale }: any) => {
  let bodyElement: any
  const [contentfulNavigation, setContentfulNavigation] =
    useState<NavigationResult | null>(null)
  const [languageSwitcherOpen, setLanguageSwitcherOpen] = useState(false)

  const navEntryLocale = locale ? locale : 'en-US'

  const handleAccordionOpen = (event: MouseEvent) => {
    const target = event.target as HTMLDivElement
    const idForElem = target.id.replace('-parent', '-list')
    const itemToAdd = document.getElementById(idForElem)

    if (itemToAdd) {
      if (itemToAdd.classList.contains('is-active')) {
        target.classList.remove('is-active')
        itemToAdd.classList.remove('is-active')
      } else {
        target.classList.add('is-active')
        itemToAdd.classList.add('is-active')
      }
    }
  }

  const handleLanguageSwitcherClick = (event: MouseEvent) => {
    setLanguageSwitcherOpen(!languageSwitcherOpen)
  }

  const createTextBasedOnType = (item: any) => {
    if (
      item.__typename === ModuleNames.PAGE_PRODUCT ||
      item.__typename === ModuleNames.PAGE_FLEX
    ) {
      return item.name
    } else if (item.__typename === ModuleNames.TOPIC_CTA) {
      return item.text
    }
  }

  const generateMenuColumn = (pages: any) => {
    return pages.map((page: any, n: number) => {
      let target
      const linkHref = createLinkBasedOnType(page)
      if (isExternalURL(linkHref)) {
        target = '_blank'
      } else {
        target = '_self'
      }
      return (
        <span key={page.id}>
          <a href={createLinkBasedOnType(page)} target={target} rel="noopener">
            {createTextBasedOnType(page)}
          </a>
        </span>
      )
    })
  }

  let dynamicData = footerStaticQuery.allContentfulNavMenu()

  const generateEnglishNav = () => {
    return dynamicData.allContentfulNavMenu.nodes.filter((item: any) => {
      // Default to English if not selected
      const selectedEntrylocale = item.entryLocale
        ? item.entryLocale
        : LocaleCodes.ENGLISH.name
      return (
        item.node_locale === 'en-US' &&
        selectedEntrylocale === LocaleCodes.ENGLISH.name
      )
    })
  }

  const generateLocalizedNav = () => {
    return dynamicData.allContentfulNavMenu.nodes.filter((item: any) => {
      const localeInfo = mapLocale(item.entryLocale)
      if (localeInfo && localeInfo.pageLocale) {
        const pageLocale = localeInfo.pageLocale

        return (
          navEntryLocale === pageLocale &&
          navEntryLocale === item.node_locale &&
          pageLocale === item.node_locale
        )
      }
    })
  }

  const renderAppropriateNav = () => {
    let chosenNav: any
    if (
      dynamicData &&
      dynamicData.allContentfulNavMenu &&
      Array.isArray(dynamicData.allContentfulNavMenu.nodes)
    ) {
      const englishNav = generateEnglishNav()
      if (navEntryLocale && navEntryLocale === LocaleCodes.ENGLISH.code) {
        if (englishNav && englishNav.length && englishNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(englishNav[0])
          }
          chosenNav = englishNav[0]
        }
      } else {
        const localizedNav = generateLocalizedNav()
        if (localizedNav && localizedNav.length && localizedNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(localizedNav[0])
          }
          chosenNav = localizedNav[0]
        } else if (englishNav && englishNav.length && englishNav[0]) {
          if (typeof window !== `undefined`) {
            setContentfulNavigation(englishNav[0])
          }
          chosenNav = englishNav[0]
        }
      }
    }
    return chosenNav
  }

  const makeDynamicFooterCols = (menuData: any) => {
    let footerCols
    if (
      menuData &&
      menuData.menuGroups &&
      menuData.menuGroups.length &&
      menuData.menuGroups.length > 0
    ) {
      footerCols = menuData.menuGroups.map((menuGroup: any, i: number) => {
        let groupId
        if (menuGroup.newGroupName === 'About RPA') {
          groupId = 'about-rpa'
        } else {
          groupId = menuGroup.newGroupName?.toLowerCase()
        }

        const parentLinkHref = menuGroup.parentLink
          ? createLinkBasedOnType(menuGroup.parentLink)
          : '/'

        return (
          <div
            className="MainFooterRev-column"
            key={`menuGroup-parent--${menuGroup.newGroupName}-${i}`}
          >
            <div className="MainFooterRev-column-name">
              <div
                className="MainFooterRev-column-name"
                onClick={handleAccordionOpen}
                id={`group-${i}-parent`}
              >
                {menuGroup.parentLink && parentLinkHref ? (
                  <a
                    href={parentLinkHref}
                    className="MainFooterRev-parent-link"
                  >
                    {menuGroup.newGroupName}
                  </a>
                ) : (
                  <span>{menuGroup.newGroupName}</span>
                )}
                <WrapperImage
                  className="footer-down-arrow"
                  src={downArrowWhite}
                  alt="down arrow"
                  height={16}
                  width={16}
                  threshold={650}
                />
                <WrapperImage
                  className="footer-up-arrow"
                  src={upArrowWhite}
                  alt="up arrow"
                  height={16}
                  width={16}
                  threshold={650}
                />
              </div>
            </div>
            <div id={`group-${i}-list`} className="MainFooterRev-deepList">
              {generateMenuColumn(menuGroup.pages)}
            </div>
          </div>
        )
      })
    }
    return <div className="MainFooterRev-container">{footerCols}</div>
  }

  const year = new Date().getFullYear()

  const generateLegalLinks = (legalFooter: any) => {
    return legalFooter.map((item: any, i: number) => {
      return (
        <span key={item.text}>
          <a
            href={
              item.internalLink
                ? internalLinkHandler(item.internalLink)
                : item.externalUrl
            }
            rel="noopener"
          >
            {item.text}
          </a>
        </span>
      )
    })
  }

  const generateSocials = (menuData: any) => {
    let socialUrls
    if (
      menuData &&
      menuData.socialMediaFooterOnly &&
      menuData.socialMediaFooterOnly[0] &&
      menuData.socialMediaFooterOnly[0].values &&
      menuData.socialMediaFooterOnly[0].values.length &&
      menuData.socialMediaFooterOnly[0].values.length > 0
    ) {
      socialUrls = menuData.socialMediaFooterOnly[0].values

      return socialUrls.map((social: any, n: number) => {
        const socialName = social.key.split('.')[0]

        if (socialName === 'rss') return '' // ignore RSS feed

        return (
          <a
            href={social.value ? social.value : '/'}
            target="_blank"
            rel="noopener"
            key={`social-item--${socialName}`}
          >
            {socialName === 'linkedin' && (
              <svg
                viewBox="6 7 19 19"
                xmlns="https://www.w3.org/2000/svg"
                fill="#595a5c"
                fillRule="evenodd"
              >
                <path d="M11.791 13.85v9.954H8.477v-9.955h3.314zm.211-3.074A1.59 1.59 0 0 1 11.495 12c-.345.328-.799.492-1.361.492h-.02c-.55 0-.991-.164-1.326-.492a1.646 1.646 0 0 1-.502-1.225c0-.496.172-.906.517-1.23.345-.326.795-.488 1.351-.488.556 0 1.001.162 1.336.487.335.325.506.735.512 1.23zm4.922 4.488c.128-.201.258-.378.392-.53.14-.161.33-.336.567-.523a2.61 2.61 0 0 1 .874-.437 3.982 3.982 0 0 1 1.15-.156c1.145 0 2.066.38 2.763 1.14.696.76 1.044 1.874 1.044 3.34v5.706H20.41V18.48c0-.703-.136-1.254-.407-1.652-.271-.399-.695-.598-1.27-.598-.423 0-.776.115-1.06.346a2.136 2.136 0 0 0-.638.86c-.074.2-.11.471-.11.813v5.555h-3.306c.014-2.672.02-4.839.02-6.5 0-1.66-.003-2.651-.01-2.973l-.01-.482h3.305v1.415z"></path>
              </svg>
            )}
            {socialName === 'facebook' && (
              <svg
                viewBox="6 7 19 19"
                xmlns="https://www.w3.org/2000/svg"
                fill="#595a5c"
                fillRule="evenodd"
              >
                <path d="M20.4899554,7.69196429 L20.4899554,10.34375 L18.9129464,10.34375 C18.3370507,10.34375 17.9486617,10.4642845 17.7477679,10.7053571 C17.546874,10.9464298 17.4464286,11.3080333 17.4464286,11.7901786 L17.4464286,13.6886161 L20.3895089,13.6886161 L19.9977679,16.6618304 L17.4464286,16.6618304 L17.4464286,24.2857143 L14.3727679,24.2857143 L14.3727679,16.6618304 L11.8113839,16.6618304 L11.8113839,13.6886161 L14.3727679,13.6886161 L14.3727679,11.4988839 C14.3727679,10.253342 14.7209787,9.28739182 15.4174107,8.60100446 C16.1138428,7.9146171 17.0412889,7.57142857 18.1997768,7.57142857 C19.1841567,7.57142857 19.9475419,7.61160674 20.4899554,7.69196429 Z"></path>
              </svg>
            )}

            {socialName === 'twitter' && (
              <svg
                viewBox="6 7 19 19"
                xmlns="https://www.w3.org/2000/svg"
                fill="#595a5c"
                fillRule="evenodd"
              >
                <path d="M23.915 11.67a6.798 6.798 0 0 1-1.627 1.677 9.217 9.217 0 0 1-.372 3.028 9.42 9.42 0 0 1-1.16 2.497 9.812 9.812 0 0 1-1.853 2.114c-.717.613-1.58 1.102-2.592 1.467a9.484 9.484 0 0 1-3.244.547 9.01 9.01 0 0 1-4.982-1.456c.234.026.495.04.783.04 1.507 0 2.85-.462 4.028-1.386a3.163 3.163 0 0 1-1.888-.648 3.173 3.173 0 0 1-1.145-1.602c.22.033.425.05.612.05.288 0 .573-.037.854-.11a3.192 3.192 0 0 1-1.863-1.12 3.135 3.135 0 0 1-.738-2.065v-.04c.455.254.944.392 1.466.412A3.24 3.24 0 0 1 9.14 13.92a3.161 3.161 0 0 1-.392-1.547c0-.59.147-1.135.442-1.638a9.197 9.197 0 0 0 2.958 2.396 9.04 9.04 0 0 0 3.731 1 3.604 3.604 0 0 1-.08-.744c0-.897.317-1.662.95-2.295a3.127 3.127 0 0 1 2.295-.95c.937 0 1.727.342 2.37 1.025a6.362 6.362 0 0 0 2.06-.783 3.15 3.15 0 0 1-1.427 1.788 6.464 6.464 0 0 0 1.868-.502z" />
              </svg>
            )}

            {socialName === 'youtube' && (
              <svg
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.1035 4.45938C22.0501 4.71237 22.7955 5.45794 23.0484 6.40435C23.5081 8.12023 23.5081 11.6998 23.5081 11.6998C23.5081 11.6998 23.5081 15.2795 23.0484 16.9952C22.7955 17.9416 22.0501 18.6874 21.1035 18.9402C19.3878 19.4 12.5079 19.4 12.5079 19.4C12.5079 19.4 5.62829 19.4 3.91259 18.9402C2.966 18.6874 2.22061 17.9416 1.96762 16.9952C1.50781 15.2795 1.50781 11.6998 1.50781 11.6998C1.50781 11.6998 1.50781 8.12023 1.96762 6.40435C2.22061 5.45794 2.966 4.71237 3.91259 4.45938C5.62829 3.99976 12.5079 3.99976 12.5079 3.99976C12.5079 3.99976 19.3878 3.99976 21.1035 4.45938ZM16.0236 11.7L10.308 8.39966V14.9999L16.0236 11.7Z"
                  fill="white"
                  fillOpacity="0.301961"
                />
              </svg>
            )}
            {/*
            {socialName === 'rss' && (
              <svg
                viewBox="6 7 19 19"
                xmlns="https://www.w3.org/2000/svg"
                fill="#595a5c"
                fillRule="evenodd"
              >
                <path d="M12.223 19.705c.375.375.563.83.563 1.366 0 .536-.188.991-.563 1.366a1.86 1.86 0 0 1-1.366.563 1.86 1.86 0 0 1-1.366-.563 1.86 1.86 0 0 1-.562-1.366c0-.535.187-.99.562-1.366a1.86 1.86 0 0 1 1.366-.562c.536 0 .991.187 1.366.562zm5.706 2.602a.608.608 0 0 1-.171.482.592.592 0 0 1-.472.211H15.93a.617.617 0 0 1-.432-.166.611.611 0 0 1-.201-.417 6.247 6.247 0 0 0-1.853-3.932 6.247 6.247 0 0 0-3.933-1.853.611.611 0 0 1-.417-.201.617.617 0 0 1-.165-.432v-1.356c0-.194.07-.352.21-.472A.585.585 0 0 1 9.572 14h.05a9.007 9.007 0 0 1 3.074.809 8.882 8.882 0 0 1 2.602 1.823 8.882 8.882 0 0 1 1.823 2.601c.452.978.722 2.002.809 3.074zm5.142.02a.58.58 0 0 1-.18.472.594.594 0 0 1-.462.201h-1.437a.627.627 0 0 1-.447-.176.6.6 0 0 1-.196-.427 11.23 11.23 0 0 0-1.014-4.103 11.545 11.545 0 0 0-2.326-3.375 11.545 11.545 0 0 0-3.375-2.325 11.377 11.377 0 0 0-4.103-1.025.6.6 0 0 1-.427-.196.615.615 0 0 1-.175-.437V9.5c0-.188.067-.342.2-.462.121-.12.268-.18.442-.18h.03c1.755.086 3.434.488 5.038 1.204 1.604.717 3.028 1.701 4.274 2.954a14.081 14.081 0 0 1 2.953 4.274 13.938 13.938 0 0 1 1.205 5.037z" />
              </svg>
            )} */}

            {socialName === 'slack' && (
              <svg
                viewBox="6 7 19 19"
                fill="#595a5c"
                version="1.1"
                xmlns="https://www.w3.org/2000/svg"
                fillRule="evenodd"
              >
                <path d="M22.9006696,15.3660714 C23.3158503,15.3660714 23.662387,15.5016728 23.9402902,15.7728795 C24.2181934,16.0440862 24.3571429,16.3839265 24.3571429,16.7924107 C24.3571429,17.4419675 24.045762,17.877231 23.4229911,18.0982143 L21.6953125,18.6908482 L22.2578125,20.3683036 C22.3046877,20.5089293 22.328125,20.6662938 22.328125,20.8404018 C22.328125,21.235493 22.1875014,21.5770075 21.90625,21.8649554 C21.6249986,22.1529032 21.2868323,22.296875 20.8917411,22.296875 C20.5770074,22.296875 20.2907379,22.2064741 20.0329241,22.0256696 C19.7751103,21.8448652 19.5959826,21.6037961 19.4955357,21.3024554 L18.9430804,19.6450893 L15.8292411,20.7098214 L16.3816964,22.3571429 C16.4352681,22.5178579 16.4620536,22.6752224 16.4620536,22.8292411 C16.4620536,23.2243323 16.32143,23.5658468 16.0401786,23.8537946 C15.7589272,24.1417425 15.4174127,24.2857143 15.015625,24.2857143 C14.7008913,24.2857143 14.4162959,24.1953134 14.1618304,24.0145089 C13.9073648,23.8337045 13.7299112,23.5926354 13.6294643,23.2912946 L13.0770089,21.6540179 L11.5401786,22.1863839 C11.3459812,22.2466521 11.1785721,22.2767857 11.0379464,22.2767857 C10.6294622,22.2767857 10.2896219,22.1428585 10.0184152,21.875 C9.74720847,21.6071415 9.61160714,21.2689753 9.61160714,20.8604911 C9.61160714,20.5457574 9.70368211,20.261162 9.88783482,20.0066964 C10.0719875,19.7522309 10.3113825,19.5747773 10.6060268,19.4743304 L12.1729911,18.9419643 L11.1183036,15.7979911 L9.55133929,16.3404018 C9.37723127,16.3939735 9.21651859,16.4207589 9.06919643,16.4207589 C8.66740871,16.4207589 8.32924244,16.2851576 8.0546875,16.0139509 C7.78013256,15.7427442 7.64285714,15.406252 7.64285714,15.0044643 C7.64285714,14.6897306 7.73493211,14.4051352 7.91908482,14.1506696 C8.10323753,13.8962041 8.34263246,13.7187505 8.63727679,13.6183036 L10.2142857,13.0859375 L9.68191964,11.4888393 C9.62834795,11.3281242 9.6015625,11.1707597 9.6015625,11.0167411 C9.6015625,10.6149533 9.74218609,10.2717648 10.0234375,9.98716518 C10.3046889,9.70256554 10.6462033,9.56026786 11.0479911,9.56026786 C11.3627248,9.56026786 11.6473202,9.65066874 11.9017857,9.83147321 C12.1562513,10.0122777 12.3337049,10.2533467 12.4341518,10.5546875 L12.9765625,12.1618304 L16.0904018,11.1071429 L15.5479911,9.5 C15.4944194,9.33928491 15.4676339,9.18192041 15.4676339,9.02790179 C15.4676339,8.63281052 15.6099316,8.29129608 15.8945312,8.00334821 C16.1791309,7.71540035 16.5189712,7.57142857 16.9140625,7.57142857 C17.2287962,7.57142857 17.5150657,7.66350354 17.7728795,7.84765625 C18.0306933,8.03180896 18.2098209,8.27120388 18.3102679,8.56584821 L18.8426339,10.1830357 L20.4698661,9.63058036 C20.6104918,9.59040158 20.7544635,9.5703125 20.9017857,9.5703125 C21.3035734,9.5703125 21.646762,9.70256564 21.9313616,9.96707589 C22.2159612,10.2315861 22.3582589,10.561382 22.3582589,10.9564732 C22.3582589,11.257814 22.2578135,11.5306907 22.0569196,11.7751116 C21.8560258,12.0195325 21.6082604,12.1919638 21.3136161,12.2924107 L19.7366071,12.8348214 L20.7912946,16.0089286 L22.4386161,15.4464286 C22.5993312,15.3928569 22.7533475,15.3660714 22.9006696,15.3660714 Z M14.9252232,17.9977679 L18.0390625,16.9430804 L16.984375,13.7790179 L13.8705357,14.8537946 L14.9252232,17.9977679 Z"></path>
              </svg>
            )}

            {socialName === 'instagram' && (
              <svg
                viewBox="0 0 24 24"
                version="1.1"
                xmlns="https://www.w3.org/2000/svg"
              >
                <g
                  id="24x24/social/Instagram"
                  stroke="none"
                  strokeWidth="1"
                  fill="none"
                  fillRule="evenodd"
                >
                  <path
                    d="M14.2220982,14.3649554 C14.8359406,13.751113 15.1428571,13.010421 15.1428571,12.1428571 C15.1428571,11.2752933 14.8359406,10.5346013 14.2220982,9.92075893 C13.6082559,9.30691657 12.8675639,9 12,9 C11.1324361,9 10.3917441,9.30691657 9.77790179,9.92075893 C9.16405943,10.5346013 8.85714286,11.2752933 8.85714286,12.1428571 C8.85714286,13.010421 9.16405943,13.751113 9.77790179,14.3649554 C10.3917441,14.9787977 11.1324361,15.2857143 12,15.2857143 C12.8675639,15.2857143 13.6082559,14.9787977 14.2220982,14.3649554 Z M15.4252232,8.71763393 C16.3664482,9.65885887 16.8370536,10.8005885 16.8370536,12.1428571 C16.8370536,13.4851258 16.3664482,14.6268554 15.4252232,15.5680804 C14.4839983,16.5093053 13.3422686,16.9799107 12,16.9799107 C10.6577314,16.9799107 9.51600173,16.5093053 8.57477679,15.5680804 C7.63355184,14.6268554 7.16294643,13.4851258 7.16294643,12.1428571 C7.16294643,10.8005885 7.63355184,9.65885887 8.57477679,8.71763393 C9.51600173,7.77640898 10.6577314,7.30580357 12,7.30580357 C13.3422686,7.30580357 14.4839983,7.77640898 15.4252232,8.71763393 Z M17.8314732,6.31138393 C18.0524565,6.53236718 18.1629464,6.79836154 18.1629464,7.109375 C18.1629464,7.42038846 18.0524565,7.68638282 17.8314732,7.90736607 C17.61049,8.12834932 17.3444956,8.23883929 17.0334821,8.23883929 C16.7224687,8.23883929 16.4564743,8.12834932 16.2354911,7.90736607 C16.0145078,7.68638282 15.9040179,7.42038846 15.9040179,7.109375 C15.9040179,6.79836154 16.0145078,6.53236718 16.2354911,6.31138393 C16.4564743,6.09040068 16.7224687,5.97991071 17.0334821,5.97991071 C17.3444956,5.97991071 17.61049,6.09040068 17.8314732,6.31138393 Z M12.9330357,4.40848214 L12,4.40848214 C11.942708,4.40848214 11.6296531,4.40643603 11.0608259,4.40234375 C10.4919986,4.39825147 10.0602693,4.39825147 9.765625,4.40234375 C9.47098067,4.40643603 9.07608134,4.4187127 8.58091518,4.43917411 C8.08574901,4.45963552 7.66425025,4.50055773 7.31640625,4.56194196 C6.96856225,4.6233262 6.67596845,4.69903229 6.43861607,4.7890625 C6.02938783,4.95275379 5.66927239,5.19010261 5.35825893,5.50111607 C5.04724547,5.81212953 4.80989665,6.17224498 4.64620536,6.58147321 C4.55617515,6.81882559 4.48046906,7.11141939 4.41908482,7.45926339 C4.35770059,7.80710739 4.31677838,8.22860616 4.29631696,8.72377232 C4.27585555,9.21893849 4.26357889,9.61383781 4.25948661,9.90848214 C4.25539432,10.2031265 4.25539432,10.6348558 4.25948661,11.203683 C4.26357889,11.7725103 4.265625,12.0855652 4.265625,12.1428571 C4.265625,12.2001491 4.26357889,12.513204 4.25948661,13.0820312 C4.25539432,13.6508585 4.25539432,14.0825878 4.25948661,14.3772321 C4.26357889,14.6718765 4.27585555,15.0667758 4.29631696,15.561942 C4.31677838,16.0571081 4.35770059,16.4786069 4.41908482,16.8264509 C4.48046906,17.1742949 4.55617515,17.4668887 4.64620536,17.7042411 C4.80989665,18.1134693 5.04724547,18.4735848 5.35825893,18.7845982 C5.66927239,19.0956117 6.02938783,19.3329605 6.43861607,19.4966518 C6.67596845,19.586682 6.96856225,19.6623881 7.31640625,19.7237723 C7.66425025,19.7851566 8.08574901,19.8260788 8.58091518,19.8465402 C9.07608134,19.8670016 9.47098067,19.8792783 9.765625,19.8833705 C10.0602693,19.8874628 10.4919986,19.8874628 11.0608259,19.8833705 C11.6296531,19.8792783 11.942708,19.8772321 12,19.8772321 C12.057292,19.8772321 12.3703469,19.8792783 12.9391741,19.8833705 C13.5080014,19.8874628 13.9397307,19.8874628 14.234375,19.8833705 C14.5290193,19.8792783 14.9239187,19.8670016 15.4190848,19.8465402 C15.914251,19.8260788 16.3357497,19.7851566 16.6835938,19.7237723 C17.0314378,19.6623881 17.3240316,19.586682 17.5613839,19.4966518 C17.9706122,19.3329605 18.3307276,19.0956117 18.6417411,18.7845982 C18.9527545,18.4735848 19.1901033,18.1134693 19.3537946,17.7042411 C19.4438249,17.4668887 19.5195309,17.1742949 19.5809152,16.8264509 C19.6422994,16.4786069 19.6832216,16.0571081 19.703683,15.561942 C19.7241444,15.0667758 19.7364211,14.6718765 19.7405134,14.3772321 C19.7446057,14.0825878 19.7446057,13.6508585 19.7405134,13.0820312 C19.7364211,12.513204 19.734375,12.2001491 19.734375,12.1428571 C19.734375,12.0855652 19.7364211,11.7725103 19.7405134,11.203683 C19.7446057,10.6348558 19.7446057,10.2031265 19.7405134,9.90848214 C19.7364211,9.61383781 19.7241444,9.21893849 19.703683,8.72377232 C19.6832216,8.22860616 19.6422994,7.80710739 19.5809152,7.45926339 C19.5195309,7.11141939 19.4438249,6.81882559 19.3537946,6.58147321 C19.1901033,6.17224498 18.9527545,5.81212953 18.6417411,5.50111607 C18.3307276,5.19010261 17.9706122,4.95275379 17.5613839,4.7890625 C17.3240316,4.69903229 17.0314378,4.6233262 16.6835938,4.56194196 C16.3357497,4.50055773 15.914251,4.45963552 15.4190848,4.43917411 C14.9239187,4.4187127 14.5290193,4.40643603 14.234375,4.40234375 C13.9397307,4.39825147 13.5059552,4.40029758 12.9330357,4.40848214 Z M21.3671875,8.25111607 C21.4081103,8.97135777 21.4285714,10.2685918 21.4285714,12.1428571 C21.4285714,14.0171225 21.4081103,15.3143565 21.3671875,16.0345982 C21.2853419,17.7369877 20.7779065,19.0546828 19.8448661,19.9877232 C18.9118257,20.9207636 17.5941305,21.428199 15.8917411,21.5100446 C15.1714994,21.5509675 13.8742653,21.5714286 12,21.5714286 C10.1257347,21.5714286 8.82850063,21.5509675 8.10825893,21.5100446 C6.40586946,21.428199 5.08817431,20.9207636 4.15513393,19.9877232 C3.22209355,19.0546828 2.71465815,17.7369877 2.6328125,16.0345982 C2.59188968,15.3143565 2.57142857,14.0171225 2.57142857,12.1428571 C2.57142857,10.2685918 2.59188968,8.97135777 2.6328125,8.25111607 C2.71465815,6.54872661 3.22209355,5.23103145 4.15513393,4.29799107 C5.08817431,3.36495069 6.40586946,2.85751529 8.10825893,2.77566964 C8.82850063,2.73474682 10.1257347,2.71428571 12,2.71428571 C13.8742653,2.71428571 15.1714994,2.73474682 15.8917411,2.77566964 C17.5941305,2.85751529 18.9118257,3.36495069 19.8448661,4.29799107 C20.7779065,5.23103145 21.2853419,6.54872661 21.3671875,8.25111607 Z"
                    id="icon"
                    fill="#595A5C"
                  ></path>
                </g>
              </svg>
            )}
          </a>
        )
      })
    }
  }

  const toggle = (e: any) => {
    if (languageSwitcherOpen) {
      setLanguageSwitcherOpen(false)
    }
  }

  useEffect(() => {
    if (typeof document !== undefined && typeof window !== undefined) {
      if (languageSwitcherOpen) window.addEventListener('click', toggle, false)
      return () => {
        return window.removeEventListener('click', toggle, false)
      }
    }
  }, [languageSwitcherOpen])

  useEffect(() => {
    renderAppropriateNav()
  }, [locale])

  const generateLanguageSelect = () => {
    return LanguageSelect.rows.map((row: any, i: number) => {
      if (row.code !== navEntryLocale) {
        return (
          <span key={`language-select-footer--${i}`}>
            <a
              data-language={row.lang}
              href={row.href}
              rel="noopener"
            >
              {row.title}
            </a>
          </span>
        )
      }
    })
  }

  const dynamicFooter = () => {
    let menuData, languageDisplayName
    if (typeof window == `undefined`) {
      menuData = renderAppropriateNav()
    } else {
      menuData = contentfulNavigation
    }

    languageDisplayName = returnMatchingLocaleInfo(navEntryLocale)
    if (languageDisplayName === LocaleCodes.ENGLISH.name) {
      languageDisplayName = 'English'
    }

    return (
      <FooterWrapper minimalFooter={minimalFooter}>
        <footer className="MainFooterRev MainFooterRev--rebranded">
          {!minimalFooter && makeDynamicFooterCols(menuData)}
          <section className="MainFooterRev-minifooter">
            <div className="MainFooterRev-minifooter--socialContainer">
              <div className="MainFooterRev-minifooter--logo">
                <WrapperImage
                  src={logoWhite}
                  alt="UiPath Logo"
                  height={91}
                  width={32}
                  threshold={650}
                />
              </div>

              <div className="SecondaryFooter-container-mobile">
                <div
                  className="SecondaryFooter-languageSwitcher"
                  onClick={handleLanguageSwitcherClick}
                >
                  <div
                    className={`SecondaryFooter-languageList ${
                      languageSwitcherOpen ? 'is-active' : ''
                    }`}
                    id="SecondaryFooter-Language-List-Container"
                  >
                    {generateLanguageSelect()}
                  </div>

                  <div className="SecondaryFooter-languageList-select">
                    <span id="SecondaryFooter-languageList-text-desktop">
                      {languageDisplayName}
                    </span>
                    {minimalFooter ? (
                      <svg
                        width="6"
                        height="5"
                        viewBox="0 0 6 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="footer-down-caret"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0.239957 1.37345C0.176853 1.31086 0.141357 1.22566 0.141357 1.13678C0.141357 1.0479 0.176853 0.962705 0.239957 0.900116L0.37329 0.766782C0.436007 0.701677 0.5229 0.665472 0.61329 0.666782H5.38662C5.47701 0.665472 5.56391 0.701677 5.62662 0.766782L5.75996 0.900116C5.82306 0.962705 5.85856 1.0479 5.85856 1.13678C5.85856 1.22566 5.82306 1.31086 5.75996 1.37345L3.23329 3.90012C3.1728 3.96457 3.08835 4.00113 2.99996 4.00113C2.91157 4.00113 2.82711 3.96457 2.76662 3.90012L0.239957 1.37345Z"
                          fill="white"
                        />
                      </svg>
                    ) : (
                      <>
                        <WrapperImage
                          className="footer-down-arrow"
                          src={downArrowWhite}
                          alt="down arrow"
                          height={16}
                          width={16}
                          threshold={650}
                        />
                        <WrapperImage
                          className="footer-closeButton"
                          style={{ display: 'none' }}
                          src={closeIconBlue}
                          alt="close icon"
                          height={16}
                          width={16}
                          threshold={650}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="MainFooterRev-minifooter--socialIcons">
                <div className="SecondaryFooter-column">
                  <div className="MainFooterRev-social-networks">
                    {generateSocials(menuData)}
                  </div>
                </div>
              </div>
            </div>

            <div className="MainFooterRev-minifooter--bottomText">
              <div className="MainFooterRev-minifooter--legalLinks">
                {/* We need to wrap the stateful component in a div to prevent it it from rerendering the parent element.
                The View Cookie Settings is needed to interact with the OT script loaded from GTM and would break if rerendered. */}
                {menuData?.legalFooterOnly?.length > 0 && (
                  <div>{generateLegalLinks(menuData.legalFooterOnly)}</div>
                )}
                <span key="View Cookie Settings">
                  <a
                    id="ot-sdk-btn"
                    className="ot-sdk-show-settings"
                    rel="noopener"
                  >
                    View Cookie Settings
                  </a>
                </span>
              </div>
              <div className="MainFooterRev-minifooter--copyright">
                <span>{`© 2005-${year} UiPath. All rights reserved.`}</span>
              </div>
            </div>
          </section>
        </footer>
      </FooterWrapper>
    )
  }

  if (dynamicData) {
    return dynamicFooter()
  } else {
    return null
  }
}

export default Footer
